/**
 * media-control is meant to extend the mediaDevices api methods such as
 * `getUserMedia`, `enumerateDevices` and events on media streams and tracks.
 * The final goal is to have a library that gives a stable and robust way to use
 * these methods while guaranteeing that the developer has better control over
 * which devices are delivered, fail, or exist.
 *
 * @packageDocumentation
 */
import { areMultipleFacingModeSupported, areTracksEnabled, createTrackDevicesChanges, deviceChanged, findAudioInputDevices, findAudioOutputDevices, findCurrentAudioOutputId, findCurrentVideoInputDeviceIdFromStream, findDeviceWithDeviceId, findDevicesByKind, findMediaInputFromMediaStreamTrack, findMediaInputFromStream, findPermissionGrantedDevices, findVideoInputDevices, getDevices, getInputDevicePermissionState, hasAnyGrantedInput, hasAnyInputs, hasAudioInputs, hasAudioOrVideoInputs, hasChangedInput, hasRequestingDevice, hasVideoInputs, interpretCurrentFacingMode, isRequestedInputDevice, isRequestedInputTrack, isRequestedResolution, isStreamingRequestedDevices, isStreamingRequestedDevicesBase, isStreamingRequestingDevice, isTrackMutedOrEnded, muteStreamTrack, shouldRequestDevice, stopMediaStream, toKey, toMediaDeviceInfo, toMediaDeviceInfoLike, toMediaDeviceInputKind, } from './devices';
import { compareDevices, createIndexedDevices, findDevice, getDeviceId, isAudioInput, isAudioOutput, isDeviceGranted, isVideoInput, reverseDeviceId, } from './utils';
import { applyConstraints, extractConstrainDevice, extractConstraints, extractConstraintsWithKeys, findDeviceFromConstraints, getConstraintsHandlers, getFacingModeFromConstraintString, getValueFromConstrainNumber, isExactDeviceConstraint, mergeConstraints, relaxInputConstraint, resolveMediaDeviceConstraints, } from './constraints';
import { eventEmitter, MediaEventType } from './eventEmitter';
import { handleMediaStream, createStreamTrackEventSubscriptions, } from './streams';
import { MediaDeviceFailure, MediaDeviceKinds, } from './types';
import { isFacingMode, isIndexedDevices, isInputConstraintSet, isMediaDeviceInfo, isMediaDeviceInfoArray, isMediaStreamTrack, } from './typeGuards';
import { createStreamTrackMap } from './streamTrackMap';
export { setLogger } from './logger';
const state = () => {
    const { dispatch, subscriber } = eventEmitter();
    const streamTrackMap = createStreamTrackMap();
    const { getDefaultConstraints, setDefaultConstraints } = getConstraintsHandlers();
    const { getUserMedia } = handleMediaStream({
        dispatch,
        getDefaultConstraints,
        streamTrackMap,
    });
    /**
     * Subscription media events
     *
     * @beta
     */
    const subscribe = (listener) => {
        return subscriber(listener, deviceChanged(event => {
            // Can be used to keep a store in sync
            dispatch({
                type: MediaEventType.DevicesChanged,
                devices: event.devices,
            });
            if (!hasAudioOrVideoInputs(event.devices)) {
                return dispatch({
                    type: MediaEventType.NoInputDevices,
                    devices: event.devices,
                });
            }
            if (event.found.length) {
                // Can be used to trigger user notification of a potentially better device being available
                dispatch({
                    type: MediaEventType.DevicesFound,
                    devicesFound: event.found,
                    devices: event.devices,
                });
            }
            if (event.lost.length) {
                // Can be used to trigger user notification of no longer seen devices
                dispatch({
                    type: MediaEventType.DevicesLost,
                    devicesLost: event.lost,
                    devices: event.devices,
                });
                for (const device of event.lost) {
                    if (streamTrackMap.has(device)) {
                        // Can be used to trigger user warning that a device they were using is lost
                        dispatch({ type: MediaEventType.DeviceLost, device });
                    }
                }
            }
        }));
    };
    return {
        getUserMedia,
        setDefaultConstraints,
        subscribe,
    };
};
const { 
/**
 * Get MediaStream with provided {@link MediaDeviceRequest | input constraints}
 */
getUserMedia, 
/**
 * Set default media stream constraints
 *
 * A {@link
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamConstraints |
 * MediaStreamConstraints} object specifying the types of media to request, along with any requirements for each type.
 */
setDefaultConstraints, 
/**
 * Subscribe media events
 */
subscribe, } = state();
export { getUserMedia, setDefaultConstraints, subscribe };
export { MediaDeviceFailure, MediaDeviceKinds, MediaEventType, applyConstraints, areMultipleFacingModeSupported, areTracksEnabled, compareDevices, createIndexedDevices, createStreamTrackEventSubscriptions, createTrackDevicesChanges, deviceChanged, extractConstrainDevice, extractConstraints, extractConstraintsWithKeys, findAudioInputDevices, findAudioOutputDevices, findCurrentAudioOutputId, findCurrentVideoInputDeviceIdFromStream, findDevice, findDeviceFromConstraints, findDeviceWithDeviceId, findDevicesByKind, findMediaInputFromMediaStreamTrack, findMediaInputFromStream, findPermissionGrantedDevices, findVideoInputDevices, getDeviceId, getDevices, getFacingModeFromConstraintString, getInputDevicePermissionState, getValueFromConstrainNumber, hasAnyGrantedInput, hasAnyInputs, hasAudioInputs, hasAudioOrVideoInputs, hasChangedInput, hasRequestingDevice, hasVideoInputs, interpretCurrentFacingMode, isAudioInput, isAudioOutput, isDeviceGranted, isExactDeviceConstraint, isFacingMode, isIndexedDevices, isInputConstraintSet, isMediaDeviceInfo, isMediaDeviceInfoArray, isMediaStreamTrack, isRequestedInputDevice, isRequestedInputTrack, isRequestedResolution, isStreamingRequestedDevices, isStreamingRequestedDevicesBase, isStreamingRequestingDevice, isTrackMutedOrEnded, isVideoInput, mergeConstraints, muteStreamTrack, relaxInputConstraint, resolveMediaDeviceConstraints, reverseDeviceId, shouldRequestDevice, stopMediaStream, toKey, toMediaDeviceInfo, toMediaDeviceInfoLike, toMediaDeviceInputKind, };
export * from './constants';
export * from './displayMedia';
//# sourceMappingURL=index.js.map